import { otherSide, type BriefSide, type BriefStatus } from "./briefs";
import type { CampaignStatus } from "./campaigns";

/** What a campaign is waiting for. See docs project-log/phase-1, "7. Home and next actions". */
export const NEXT_ACTION_KINDS = [
  "write_brief",
  "decide_brief",
  "answer_questions",
  "resolve_change_requests",
] as const;
export type NextActionKind = (typeof NEXT_ACTION_KINDS)[number];

/** Reads after a name: "Waiting on Chloe to write the brief". */
export const NEXT_ACTION_LABELS: Record<NextActionKind, string> = {
  write_brief: "write the brief",
  decide_brief: "decide on the brief",
  answer_questions: "answer the brief questions",
  resolve_change_requests: "resolve change requests",
};

export interface CampaignProgress {
  status: CampaignStatus;
  /** The newest revision that isn't a draft: a draft must never change the answer. */
  latestBrief: {
    status: Exclude<BriefStatus, "draft">;
    side: BriefSide;
    openQuestions: number;
  } | null;
  openChangeRequests: number;
}

export interface NextActionRule {
  kind: NextActionKind;
  /** "either": nothing has been sent yet, so either side can write the first revision. */
  side: BriefSide | "either";
  count: number;
}

export const nextActionFor = ({
  status,
  latestBrief,
  openChangeRequests,
}: CampaignProgress): NextActionRule | null => {
  if (status === "completed" || status === "archived") return null;
  if (!latestBrief) return { kind: "write_brief", side: "either", count: 1 };
  // The side that didn't send the revision decides it; the side that sent it revises it.
  if (latestBrief.status === "submitted")
    return { kind: "decide_brief", side: otherSide(latestBrief.side), count: 1 };
  if (latestBrief.status === "clarification_requested")
    return latestBrief.openQuestions > 0
      ? { kind: "answer_questions", side: latestBrief.side, count: latestBrief.openQuestions }
      : { kind: "write_brief", side: latestBrief.side, count: 1 };
  if (openChangeRequests > 0)
    return { kind: "resolve_change_requests", side: "agency", count: openChangeRequests };
  return null;
};
